import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import CardWithBorderHeading from 'components/molecules/CardWithBorderHeading';
import DescriptionList from 'components/organisms/DescriptionList';
import DescriptionListItem, { QuestionPrefix } from 'components/molecules/DescriptionListItem';
import UserInformation from '@z-components/molecules/UserInformation';
import ProfileAvatar from 'modules/users/components/ProfileAvatar';
import UserLink from 'components/atoms/UserLink';
import Location from 'components/atoms/Location';

import styles from './VerticalProQuestionAndAnswers.module.scss';

interface LocalizedStrings {
  en?: string;
  ja: string;
}

interface VerticalQuestionAndAnswerToPro {
  description: LocalizedStrings;
  proAvatarImageUrl: string;
  proLocation: LocalizedStrings;
  proName: LocalizedStrings;
  proProfileUrl: string;
  questionAndAnswers: Array<{ answer: LocalizedStrings; question: LocalizedStrings }>;
  supportedLanguages: Array<string>;
  title: LocalizedStrings;
}

export const convertJobTypeDataToLocale = (
  language: 'en' | 'ja',
  data: VerticalQuestionAndAnswerToPro
): Props => {
  const convertedToProps: Props & { [indexable: string]: unknown } = {
    description: '',
    proAvatarImageUrl: '',
    proLocation: '',
    proName: '',
    proProfileUrl: '',
    questionAndAnswers: [],
    title: '',
  };
  const simpleFieldsToLocalize = new Set(['description', 'proName', 'proLocation', 'title']);
  const arrayFieldsToLocalize = new Set(['questionAndAnswers']);

  for (const [k, v] of Object.entries(data)) {
    if (simpleFieldsToLocalize.has(k)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      convertedToProps[k] = v[language];
    } else if (arrayFieldsToLocalize.has(k) && Array.isArray(v)) {
      convertedToProps[k] = v.map(
        ({ question, answer }: { answer: LocalizedStrings; question: LocalizedStrings }) => ({
          question: question[language],
          answer: answer[language],
        })
      );
    } else {
      convertedToProps[k] = v;
    }
  }
  return convertedToProps;
};

interface Props {
  description: string;
  proAvatarImageUrl: string;
  proLocation: string;
  proName: string;
  proProfileUrl: string;
  questionAndAnswers: Array<{ answer: string; question: string }>;
  title: string;
}

const VerticalProQuestionAndAnswers: FC<Props> = ({
  description,
  proAvatarImageUrl,
  proLocation,
  proName,
  proProfileUrl,
  title,
  questionAndAnswers,
}) => {
  const { t } = useTranslation();
  return (
    <div className={styles.container}>
      <CardWithBorderHeading
        heading="h3"
        headingText={t('marketingPages:verticalProQuestionAndAnswer.proCardBorderHeadingText')}
      >
        <UserInformation avatarProps={{ avatar: proAvatarImageUrl }} avatarRenderer={ProfileAvatar}>
          <UserLink name={proName} url={proProfileUrl} nameSuffix="san" />
          <Location locationName={proLocation} />
          <p className={styles['pro-title']}>{title}</p>
        </UserInformation>
        <p className={styles['pro-description']}>{description}</p>
      </CardWithBorderHeading>
      <DescriptionList>
        {questionAndAnswers.map(({ question, answer }) => (
          <DescriptionListItem
            key={question}
            descriptionTermText={question}
            descriptionDetails={answer}
            descriptionTermPrefix={<QuestionPrefix />}
          />
        ))}
      </DescriptionList>
    </div>
  );
};

export type { Props as VerticalProQuestionAndAnswersProps };
export default VerticalProQuestionAndAnswers;
